/**
 * Waitlist schema — pre-launch demand capture on the public marketing site.
 *
 * Source of truth for the `waitlist` collection. See:
 *   - marketing/MELIO_WAITLIST_BUILD_TICKETS.md (W-1 §1, the parent spec)
 *   - src/types/acquisition.ts (the attribution block this mirrors)
 *
 * WHY THIS EXISTS: the acquisition aggregator counts `waitlistSignups` from
 * `users` — i.e. completed accounts — so CPL and CAC currently measure nearly
 * the same event. This collection is the genuinely-earlier funnel stage those
 * metrics were designed around.
 *
 * IMPORTANT (PHI / privacy):
 *   - NOTHING CLINICAL LIVES HERE. No symptoms, conditions, medications,
 *     family history, or health goals. Collecting any of those pre-account
 *     would be special-category data with no consent record, outside the
 *     assignment-scoped rules SEC-2/SEC-3 exist to enforce. If a future ticket
 *     asks for a "what brings you to Melio?" field, that is the line.
 *   - It IS personal data under GDPR. Include it in export / erasure flows.
 *   - No IP address, matching the rule set in acquisition.ts. The rate limiter
 *     keys on a salted hash instead (see joinWaitlist in python_functions).
 */

import type { Timestamp } from 'firebase/firestore';
import type { AcquisitionChannel, FirstTouchUtm } from './acquisition';
import { MAX_REFERRAL_RAW_LENGTH } from './referrers';

// ---------------------------------------------------------------------------
// LIFECYCLE
// ---------------------------------------------------------------------------

/**
 * `pending` is the only status the public form can produce. The rest are set
 * by ops once invitations start going out.
 *
 * PRE-LAUNCH NOTE: Melio is in collect-only mode — nobody is being invited
 * yet, so every entry sits at `pending` by design. The later states are
 * defined now so the shape does not have to change under a live list.
 */
export type WaitlistStatus =
  | 'pending'
  | 'invited'
  | 'converted'
  | 'declined'
  | 'expired';

/**
 * Which capture slot produced this entry. This is the field that tells you
 * which placement actually earns its space — do not collapse it into one
 * generic value.
 */
export type WaitlistCaptureSurface =
  | 'home_hero'
  | 'home_closing'
  | 'waitlist_page'
  | 'enrollment_exit'
  | 'footer'
  | 'campaign'
  /**
   * The Melio Health Check panel on the marketing site (src/marketing/
   * health-check). Only name, email and consent are sent — the questionnaire
   * answers never leave the browser, per the PHI rule at the top of this file.
   */
  | 'health_check'
  /**
   * The join-the-waitlist popup on marketing pages (src/marketing/popup).
   * Same three fields as every other surface.
   */
  | 'popup';

/**
 * Surfaces a CLIENT may claim. `sourceSurface` arrives from the browser (the
 * `?from=` param), so this list is a validation allowlist, not a catalogue —
 * the callable falls back to 'waitlist_page' for anything not in it.
 *
 * WHY 'manual_import' IS NOT HERE. It marks an entry whose consent was
 * attested by the controller rather than collected through the form. If a
 * client could claim it, anyone could POST `?from=manual_import` and forge an
 * entry that looks like a hand-verified contact — which is exactly the
 * distinction the value exists to make. It is server-only, written by the
 * Admin SDK, and must stay out of this array.
 */
export const WAITLIST_SURFACES: WaitlistCaptureSurface[] = [
  'home_hero',
  'home_closing',
  'waitlist_page',
  'enrollment_exit',
  'footer',
  'campaign',
  'health_check',
  'popup',
];

/** Set only by a server-side import, never accepted from a client. */
export type WaitlistServerSurface = 'manual_import';

export const WAITLIST_SERVER_SURFACES: WaitlistServerSurface[] = ['manual_import'];

/** Every value that can appear on a stored entry, whoever wrote it. */
export type WaitlistSurface = WaitlistCaptureSurface | WaitlistServerSurface;

/**
 * How soon they want to start. Captured because a pre-launch list inflates
 * with curiosity; this is the cheapest way to tell real demand from browsing
 * when the list is eventually worked.
 */
export type WaitlistTiming = 'ready_now' | 'three_months' | 'exploring';

export const WAITLIST_TIMINGS: WaitlistTiming[] = [
  'ready_now',
  'three_months',
  'exploring',
];

export const WAITLIST_TIMING_LABELS: Record<WaitlistTiming, string> = {
  ready_now: 'Ready now',
  three_months: 'In the next few months',
  exploring: 'Just looking for now',
};

/**
 * Which assessment they came in wanting. Mirrors `AssessmentId` on the
 * Assessments page — keep the two in step, because that page is where most of
 * this signal is captured.
 *
 * WHY THIS IS NOT OPTIONAL EXTRA COLOUR. A pre-launch list can only tell you
 * two things: how many people want in, and what they want. The second is the
 * one that survives contact with a capacity plan — it decides which panel to
 * staff for first. It was carried by the `?package=` param into /enrollment
 * until that route went behind the gate (W-0), and was dropped rather than
 * migrated. This is that signal restored on the waitlist side.
 *
 * `undecided` is a real answer, not a null. Someone who read both columns and
 * could not choose is telling you the tiers do not read as distinct, which is
 * a different problem from never being asked (`null`).
 */
export type WaitlistInterest = 'core' | 'plus' | 'undecided';

export const WAITLIST_INTERESTS: WaitlistInterest[] = ['core', 'plus', 'undecided'];

export const WAITLIST_INTEREST_LABELS: Record<WaitlistInterest, string> = {
  core: 'Core Assessment',
  plus: 'Plus Assessment',
  undecided: 'Not sure yet',
};

// ---------------------------------------------------------------------------
// FOCUS AREAS — which parts of Melio they are interested in
// ---------------------------------------------------------------------------

/**
 * Which of Melio's areas of focus a visitor is interested in. Multi-select,
 * asked on the optional step after the deposit.
 *
 * THE FRAMING IS THE COMPLIANCE, SO IT IS LOAD-BEARING. The question on the
 * form is "Which areas of Melio interest you most?" — a preference about OUR
 * SERVICE, the same category of thing as the Core/Plus question it replaced.
 * It is not "what area of your health do you want to improve", and it must not
 * drift there in a copy edit: a stated wish to improve one's mobility or
 * metabolism is a health goal, health goals reveal health status, and health
 * status is special-category data under UK GDPR — collected here pre-account
 * with only a marketing-email tick behind it, on a regulated practice whose
 * privacy notice tells visitors it collects nothing about their health. The
 * PHI rule at the top of this file exists to keep that notice true. A future
 * edit that changes the label to talk about improving anything needs the
 * privacy addendum, the consent wording and probably a DPIA to move with it.
 *
 * The values mirror the site's own pillars (src/marketing/content/pillars.ts)
 * so the chips use words the visitor read thirty seconds earlier, plus
 * metabolic health, which is a service area without a pillar of its own.
 *
 * `risk` is labelled "Genetic & inherited risk" and NOT "Family history", even
 * though that is the pillar's name, because the privacy addendum promises in
 * as many words that we collect no family history. Ticking a topic is not the
 * same as handing over your family's medical history — but a notice that
 * appears to say both is one a reasonable reader would call false, and the
 * label is the cheaper half to change.
 */
export type WaitlistFocusArea =
  | 'sleep'
  | 'movement'
  | 'nutrition'
  | 'bloodwork'
  | 'metabolic'
  | 'risk';

export const WAITLIST_FOCUS_AREAS: WaitlistFocusArea[] = [
  'sleep',
  'movement',
  'nutrition',
  'bloodwork',
  'metabolic',
  'risk',
];

export const WAITLIST_FOCUS_AREA_LABELS: Record<WaitlistFocusArea, string> = {
  sleep: 'Sleep',
  movement: 'Movement & mobility',
  nutrition: 'Nutrition',
  bloodwork: 'Bloodwork & biomarkers',
  metabolic: 'Metabolic health',
  risk: 'Genetic & inherited risk',
};

/**
 * Validate a client-supplied list against the allowlist: unknown values are
 * dropped, duplicates collapsed, order preserved. Returns null rather than []
 * when nothing survives, so "asked and chose none" and "never asked" store the
 * same way — there is no operational difference between the two.
 *
 * Shared with the callable's `_normalise_focus_areas` in spirit; each side
 * validates independently because the client's list is never trusted.
 */
export function normaliseFocusAreas(raw: unknown): WaitlistFocusArea[] | null {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<WaitlistFocusArea>();
  raw.forEach((value) => {
    if (typeof value !== 'string') return;
    const key = value.trim().toLowerCase();
    if ((WAITLIST_FOCUS_AREAS as string[]).includes(key)) seen.add(key as WaitlistFocusArea);
  });
  return seen.size ? Array.from(seen) : null;
}

// ---------------------------------------------------------------------------
// REFERRAL ATTRIBUTION — who gets the credit
// ---------------------------------------------------------------------------

/**
 * How the referral reached us. Worth separating because the two have very
 * different data quality: a `link` referral is a code the referrer themselves
 * handed out, a `typed` one is the visitor's recollection of a name.
 */
export type WaitlistReferralMethod = 'link' | 'typed';

/**
 * Whether the answer resolved to a person in the `referrers` registry.
 *
 * `ambiguous` is NOT a variant of `unmatched`. It means the text matched more
 * than one active referrer — two people called Jon — and the server refused to
 * pick. Guessing would credit the wrong colleague with a bonus, which is worse
 * than asking someone to look. Ops resolves both states the same way, but only
 * `ambiguous` says the registry itself needs an alias tidied up.
 */
export type WaitlistReferralStatus = 'none' | 'matched' | 'unmatched' | 'ambiguous';

export const WAITLIST_REFERRAL_STATUS_LABELS: Record<WaitlistReferralStatus, string> = {
  none: 'Not asked / skipped',
  matched: 'Attributed',
  unmatched: 'Needs attributing',
  ambiguous: 'More than one match',
};

export interface WaitlistReferral {
  /**
   * Exactly what the visitor typed, trimmed and capped. Kept even when the
   * lookup succeeded: if an attribution is ever disputed, the raw answer is
   * the evidence, and a resolved id alone cannot be audited.
   */
  raw: string | null;
  /** Registry code, e.g. "JONATHAN". Null until something resolves. */
  referrerId: string | null;
  /**
   * Display name AT THE TIME OF RESOLUTION, denormalised on purpose. Ops reads
   * this list far more often than the registry changes, and a rename should
   * not silently rewrite who historic entries say was credited.
   */
  referrerName: string | null;
  method: WaitlistReferralMethod | null;
  status: WaitlistReferralStatus;
  /**
   * Who decided this attribution, when it was not the server at join time:
   * a staff uid for an ops-portal attribution, or the sentinel
   * 'system:referral-backfill' for one settled by
   * functions/seed/backfillReferralAttribution.js.
   *
   * Never blank on a re-attribution. An attribution decides who gets paid, so
   * it carries the name of whoever decided it — a script included.
   */
  assignedByUid?: string | null;
  assignedAt?: Timestamp | null;
}

/** An entry that was never asked, or whose visitor skipped the box. */
export const EMPTY_WAITLIST_REFERRAL: Omit<WaitlistReferral, 'assignedByUid' | 'assignedAt'> = {
  raw: null,
  referrerId: null,
  referrerName: null,
  method: null,
  status: 'none',
};

/** Re-exported so form code has one import for the field's constraints. */
export { MAX_REFERRAL_RAW_LENGTH };

// ---------------------------------------------------------------------------
// DOCUMENT SHAPE
// ---------------------------------------------------------------------------

export interface WaitlistEntry {
  // --- Tier 0: collected on the form (required) ---------------------------
  /** Lowercased + trimmed server-side. The dedupe key. */
  email: string;
  firstName: string;

  // --- Tier 1: optional qualifiers, asked after the email is banked -------
  /**
   * Outward code ONLY — "SW1", never the full postcode. A full UK postcode
   * identifies ~15 households and there is no operational need for that
   * precision: this answers "where is demand clustering / which city do we
   * open in first", not "can we reach this address".
   */
  outwardCode: string | null;
  timing: WaitlistTiming | null;
  /** Interested in joining alongside a partner or family member. */
  household: boolean | null;
  /**
   * Which assessment they were looking at. Set at join time from the
   * Assessments-page CTA, or asked in step 2 when we arrived without one.
   * `null` means never asked, which is NOT the same as `undecided`.
   */
  interest: WaitlistInterest | null;
  /**
   * Which of Melio's areas interest them (multi-select, step 2). Null when
   * never asked or nothing chosen. See the framing note on WaitlistFocusArea —
   * this is service interest, deliberately not a health goal.
   */
  focusAreas: WaitlistFocusArea[] | null;

  /**
   * Who they say sent them. The text is theirs; `referrerId` is the server's
   * resolution of it and is never accepted from the client — a client-supplied
   * id is a bonus someone else earns.
   */
  referral: WaitlistReferral;

  // --- Tier 2: server-derived. Client input is never trusted here ---------
  acquisition: WaitlistAcquisition;
  consent: WaitlistConsent;
  sourceSurface: WaitlistSurface;

  // --- Deposit (2026-09-14) — written only by the payment callables ------
  /** Absent on entries created before the deposit; null while unpaid. */
  payment?: WaitlistPayment | null;

  // --- Lifecycle ----------------------------------------------------------
  status: WaitlistStatus;
  invitedAt: Timestamp | null;
  convertedUid: string | null;

  createdAt: Timestamp;
  updatedAt: Timestamp;
  schemaVersion: 1;
}

/** The deposit that reserves a place, taken by Stripe Checkout. £100 from 2026-09-14; £50 from 2026-10-09 (issue #23). */
export const WAITLIST_DEPOSIT_GBP = 50;

export type WaitlistPaymentStatus = 'checkout_started' | 'paid' | 'refunded' | 'partially_refunded';

export interface WaitlistPayment {
  status: WaitlistPaymentStatus;
  amountGbp: number;
  currency: 'GBP';
  /** 'dummy' only ever on a non-production project with no Stripe key. */
  mode: 'stripe' | 'dummy';
  stripeCheckoutSessionId?: string | null;
  stripePaymentIntentId?: string | null;
  checkoutStartedAt?: Timestamp | null;
  paidAt?: Timestamp | null;
  refundedAt?: Timestamp | null;
  amountRefundedGbp?: number | null;
}

/**
 * Mirrors `UserAcquisition` minus the fields that only exist once someone has
 * signed up (`daysFromFirstTouchToSignup`, `promoCodeAtSignup`). Rehydrated
 * server-side from the `melio_firsttouch` cookie the client forwards.
 */
export interface WaitlistAcquisition {
  channel: AcquisitionChannel;
  campaignId: string | null;
  firstTouchAt: Timestamp | null;
  firstTouchPath: string;
  /** Hostname only — never a full url with query. */
  firstTouchReferrer: string | null;
  firstTouchUtm: FirstTouchUtm;
  promoCode: string | null;
  referrerUid: string | null;
}

/**
 * Marketing-email consent. Explicit, unticked by default, and unbundled from
 * anything else — UK PECR soft opt-in does not apply here because there is no
 * prior sale to rely on.
 */
export interface WaitlistConsent {
  marketingEmail: boolean;
  /** WAITLIST_CONSENT_VERSION at the time of capture. */
  version: string;
  capturedAt: Timestamp;
}

/**
 * Bump whenever the consent sentence changes materially, so old records keep
 * pointing at the text that was actually shown. Same convention as
 * ONBOARDING_CONSENT_VERSION in src/types/consent.ts.
 *
 * v1 text: "Email me about Melio membership. We'll only write about places
 * opening and the practice itself — never more than monthly, and you can
 * unsubscribe in one click."
 */
export const WAITLIST_CONSENT_VERSION = '2026-08-waitlist-v1';

// ---------------------------------------------------------------------------
// CALLABLE CONTRACT
// ---------------------------------------------------------------------------

/** Request payload for the `joinWaitlist` callable. */
export interface JoinWaitlistRequest {
  email: string;
  firstName: string;
  consentMarketingEmail: boolean;
  /** Narrow on purpose: a client cannot claim a server-only surface. */
  sourceSurface: WaitlistCaptureSurface;
  /** Serialised FirstTouchPayload from the melio_firsttouch cookie, if any. */
  firstTouch?: unknown;
  outwardCode?: string | null;
  timing?: WaitlistTiming | null;
  household?: boolean | null;
  interest?: WaitlistInterest | null;
  focusAreas?: WaitlistFocusArea[] | null;
  /**
   * Raw contents of the "who told you about us?" box, or the code carried by a
   * `?ref=` link. Free text by design — the server resolves it. Only the text
   * crosses the wire; the resolved referrer id is never taken from a client.
   */
  referredBy?: string | null;
  /** Whether `referredBy` came from a link or was typed. Advisory only. */
  referralMethod?: WaitlistReferralMethod | null;
}

/**
 * Response is deliberately identical whether the email was new or already on
 * the list. A distinguishable response turns the endpoint into an existence
 * oracle for the list, which is a real disclosure on a health-adjacent
 * audience.
 */
export interface JoinWaitlistResponse {
  success: boolean;
  /** Opaque handle for the deposit and the follow-up (Tier 1) submission. */
  ref?: string;
  error?: string;
}

/** `createWaitlistCheckoutSession` — opens Stripe Checkout for the deposit on an entry. */
export interface StartWaitlistCheckoutRequest {
  ref: string;
  successUrl: string;
  cancelUrl: string;
}

export interface StartWaitlistCheckoutResponse {
  success: boolean;
  /** Stripe-hosted Checkout page to send the visitor to. */
  url?: string;
  /** Non-production project with no Stripe key: the entry was marked paid without a charge. */
  dummy?: boolean;
  alreadyPaid?: boolean;
  error?: string;
}

/** `confirmWaitlistPayment` — verifies a returned Checkout Session with Stripe. */
export interface ConfirmWaitlistPaymentResponse {
  success: boolean;
  ref?: string;
  error?: string;
}

/**
 * Outward code = the part of a UK postcode before the space. Between two and
 * four characters: letter(s), digit(s), optional trailing letter.
 * Deliberately permissive — this is a shape check to stop a full postcode or
 * free text being stored, not an address validator.
 */
export const OUTWARD_CODE_PATTERN = /^[A-Z]{1,2}\d[A-Z\d]?$/;

/** Normalise user input to a storable outward code, or null if unusable. */
export function normaliseOutwardCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Take only what precedes the first space, so pasting a full postcode
  // ("SW1A 1AA") silently degrades to the outward code rather than failing.
  const head = raw.trim().toUpperCase().split(/\s+/)[0] ?? '';
  if (!head) return null;
  return OUTWARD_CODE_PATTERN.test(head) ? head : null;
}

/**
 * Normalise an `?interest=` URL param to a storable value, or null.
 *
 * Lives here rather than in the page so the client and the callable agree on
 * exactly one definition of a valid value — the same reason
 * normaliseOutwardCode is not inlined at its call site.
 */
export function normaliseInterest(
  raw: string | null | undefined,
): WaitlistInterest | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  return (WAITLIST_INTERESTS as string[]).includes(value)
    ? (value as WaitlistInterest)
    : null;
}
